import TextField, { TextFieldProps } from '@mui/material/TextField'
import { FieldTitle, useInput, type Validator } from 'ra-core'
import { FunctionComponent, ReactNode } from 'react'
import { InputHelperText, sanitizeInputRestProps } from 'react-admin'
import { NumericFormat, type InputAttributes, type NumericFormatProps } from 'react-number-format'
import { standardDecimalFormat } from '~/helpers'

const convertStringToNumber = (value) => {
  const stripped = value.replaceAll(/[^\d,]/g, '')
  const float = parseFloat(stripped.replace(',', '.'))

  return isNaN(float) ? null : float
}

const FormattedNumberInput: FunctionComponent<FormattedNumberInputProps> = ({
  format,
  helperText,
  label,
  margin = 'dense',
  onBlur,
  onFocus,
  onChange,
  options = {},
  parse = convertStringToNumber,
  resource,
  source,
  step = 'any',
  min,
  max,
  validate,
  variant = 'filled',
  inputProps: overrideInputProps,
  ...rest
}) => {
  // react-admin 5's useInput returns react-hook-form's field/fieldState
  // rather than final-form's input/meta.
  const {
    id,
    field,
    isRequired,
    fieldState: { error, isTouched },
  } = useInput({
    format,
    onBlur,
    onChange,
    parse,
    resource,
    source,
    type: 'text',
    validate,
    ...rest,
  })

  const inputProps = { ...overrideInputProps, step, min, max }

  return (
    <NumericFormat
      onFocus={onFocus}
      customInput={TextField}
      {...standardDecimalFormat}
      id={id}
      {...field}
      variant={variant}
      error={!!(isTouched && error)}
      helperText={<InputHelperText error={error?.message} helperText={helperText} />}
      label={
        <FieldTitle label={label} source={source} resource={resource} isRequired={isRequired} />
      }
      margin={margin}
      inputProps={inputProps}
      {...options}
      {...sanitizeInputRestProps(rest)}
    />
  )
}

/**
 * Props this input actually accepts.
 *
 * It used to `extends InputProps<TextFieldProps>` *and* `Omit<TextFieldProps,
 * ...>`, which TypeScript rejects outright — the two disagree on `onChange`,
 * `size` and `color`, so the whole interface resolved to an error type and
 * every call site failed to type-check. Written as an intersection instead,
 * with the NumberFormat options the callers spread in from ~/helpers/formats.
 */
export type FormattedNumberInputProps = Omit<NumericFormatProps<InputAttributes>, 'format'> & {
  /** react-admin's value transform, not NumberFormat's display mask. */
  format?: (value: any) => any
  source: string
  resource?: string
  label?: ReactNode
  helperText?: ReactNode
  validate?: Validator | Validator[]
  margin?: TextFieldProps['margin']
  variant?: TextFieldProps['variant']
  /** Extra props forwarded to the underlying MUI TextField. */
  options?: Partial<TextFieldProps>
  inputProps?: Record<string, unknown>
  step?: string | number
  min?: string | number
  max?: string | number
  onBlur?: (...args: any[]) => void
  onFocus?: (...args: any[]) => void
  onChange?: (...args: any[]) => void
  parse?: (value: any) => any
}

export default FormattedNumberInput
